import { useQuery } from '@tanstack/react-query'
import { UserRole } from '@tnf-recruit/shared'
import { apiClient, authHeader } from '../lib/api'
import { useAuth } from './useAuth'

/**
 * Syncs the Supabase-authenticated user with the backend `users` table.
 *
 * On first call after login `GET /api/auth/me` auto-creates the row if it
 * doesn't exist yet (server-side upsert).  If that fails (e.g. no email in
 * JWT), we fall back to `POST /api/auth/callback` with the email and role
 * from Supabase user metadata.
 *
 * The hook blocks via React Query's `enabled` flag until auth is fully
 * authenticated AND the `user` object is populated — which means we have
 * access to email/roles from the ID token.
 */
export function useUser() {
  const { isAuthenticated, getAccessToken, user } = useAuth()

  return useQuery({
    queryKey: ['dbUser'],
    queryFn: async () => {
      const token = await getAccessToken()

      // Try the upsert-on-read endpoint first
      try {
        const { data } = await apiClient.get('/auth/me', authHeader(token))
        const dbUser = data.data
        const metadataRole = user?.user_metadata?.role

        // If the server created a placeholder email, fix it via callback
        if (
          user?.email &&
          (dbUser?.email?.includes('@placeholder.tnf-recruit.com') ||
            (metadataRole && dbUser?.role !== metadataRole))
        ) {
          const role = metadataRole || UserRole.STUDENT
          const { data: updated } = await apiClient.post(
            '/auth/callback',
            { email: user.email, role },
            authHeader(token)
          )
          return updated.data
        }

        return dbUser
      } catch (err: any) {
        // If GET /me somehow fails with 500, try the explicit callback route
        if (user?.email) {
          const role = user?.user_metadata?.role || UserRole.STUDENT
          const { data } = await apiClient.post(
            '/auth/callback',
            { email: user.email, role },
            authHeader(token)
          )
          return data.data
        }
        throw err
      }
    },
    enabled: isAuthenticated && !!user,
    staleTime: 5 * 60 * 1000, // consider fresh for 5 minutes
    // A 403 is a decision, not a blip — retrying just delays the explanation.
    retry: (failureCount, error) =>
      (error as { response?: { status?: number } })?.response?.status === 403
        ? false
        : failureCount < 2,
  })
}
